import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../components/DashboardLayout';
import { getUsers, deleteUser } from '../services/userService';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedUsers,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(users, 10);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(Array.isArray(data) ? data : []);
      setError('');
    } catch (fetchError) {
      console.error('Error fetching users:', fetchError);
      setUsers([]);
      setError('Unable to load users right now.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await deleteUser(id);
      fetchUsers();
    } catch (deleteError) {
      console.error('Error deleting user:', deleteError);
      setError('Unable to delete this user.');
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Admin"
          title="Users"
          description="Manage admin access and keep account roles tidy and predictable."
          meta={<Chip label={`${users.length} users`} variant="outlined" />}
          action={
            <Button variant="contained" onClick={() => navigate('/users/create')}>
              Add User
            </Button>
          }
        />

        {error ? <Alert severity="error">{error}</Alert> : null}

        {isMobile ? (
          users.length ? (
            <Grid container spacing={2}>
              {paginatedUsers.map((user) => (
                <Grid item xs={12} key={user.id}>
                  <AdminSection>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {String(user.username || user.email || 'U').charAt(0).toUpperCase()}
                      </Avatar>
                      <Stack sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6">{user.username}</Typography>
                        <Typography color="text.secondary">{user.email}</Typography>
                      </Stack>
                      <Chip label={user.role || 'User'} size="small" variant="outlined" />
                    </Stack>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                      <Button variant="outlined" onClick={() => navigate(`/users/edit/${user.id}`)}>
                        Edit
                      </Button>
                      <IconButton color="error" onClick={() => handleDelete(user.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </AdminSection>
                </Grid>
              ))}
            </Grid>
          ) : (
            <AdminSection>
              <AdminEmptyState
                title="No users found"
                description="Create admin accounts here when you need to extend access to the dashboard."
              />
            </AdminSection>
          )
        ) : (
          <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length ? (
                  paginatedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                            {String(user.username || user.email || 'U').charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography sx={{ fontWeight: 600 }}>{user.username}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{user.email || '-'}</TableCell>
                      <TableCell>
                        <Chip label={user.role || 'User'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Button variant="outlined" onClick={() => navigate(`/users/edit/${user.id}`)} sx={{ mr: 1 }}>
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(user.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <AdminEmptyState
                        title="No users found"
                        description="Create admin accounts here when you need to extend access to the dashboard."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default UsersPage;
